import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dumbbell, User, Mail, Phone, Lock, ArrowRight, Check, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PW_RULES = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'At least 1 uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'At least 1 lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'At least 1 digit', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'At least 1 special char (@$!%*?&_#)', test: (p) => /[@$!%*?&_#]/.test(p) },
];

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const rules = PW_RULES.map((r) => ({ ...r, met: r.test(form.password) }));
  const allMet = rules.every((r) => r.met);
  const passwordsMatch = form.password && form.password === form.confirmPassword;
  const isValid = form.name && form.email && allMet && passwordsMatch;

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handle = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setError('');
    setLoading(true);
    try {
      await register(form);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#f8fafd] dark:bg-[#121212] relative overflow-hidden">
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-blue-200/30 dark:bg-blue-900/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-green-200/30 dark:bg-green-900/10 blur-3xl pointer-events-none" />

      <div className="relative w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
            style={{ background: 'linear-gradient(135deg, #1a73e8 0%, #4285f4 60%, #8ab4f8 100%)', boxShadow: '0 8px 24px rgba(26,115,232,0.35)' }}
          >
            <Dumbbell className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">Create Your Account</h1>
          <p className="text-xs text-[#5f6368] dark:text-[#9aa0a6] mt-0.5">List and manage your gym</p>
        </div>

        <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-xs text-red-700 dark:text-red-300 font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handle} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] block mb-1.5">Full Name</label>
              <div className="google-input-box px-3.5 py-2.5 flex items-center gap-2">
                <User className="w-4 h-4 text-[#9aa0a6] shrink-0" />
                <input
                  name="name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="w-full bg-transparent text-sm font-medium outline-none text-[#202124] dark:text-[#e8eaed]"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] block mb-1.5">Email</label>
              <div className="google-input-box px-3.5 py-2.5 flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#9aa0a6] shrink-0" />
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="owner@yourgym.com"
                  className="w-full bg-transparent text-sm font-medium outline-none text-[#202124] dark:text-[#e8eaed]"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] block mb-1.5">Phone</label>
              <div className="google-input-box px-3.5 py-2.5 flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#9aa0a6] shrink-0" />
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="9876543210"
                  className="w-full bg-transparent text-sm font-medium outline-none text-[#202124] dark:text-[#e8eaed]"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] block mb-1.5">Password</label>
              <div className="google-input-box px-3.5 py-2.5 flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#9aa0a6] shrink-0" />
                <input
                  type="password"
                  name="password"
                  required
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full bg-transparent text-sm font-medium outline-none text-[#202124] dark:text-[#e8eaed]"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] block mb-1.5">Confirm Password</label>
              <div className="google-input-box px-3.5 py-2.5 flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#9aa0a6] shrink-0" />
                <input
                  type="password"
                  name="confirmPassword"
                  required
                  value={form.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className="w-full bg-transparent text-sm font-medium outline-none text-[#202124] dark:text-[#e8eaed]"
                />
              </div>
            </div>

            {form.password && (
              <div className="rounded-xl p-3 space-y-1.5 bg-[#f1f3f4] dark:bg-[#202124] border border-[#dadce0] dark:border-[#3c4043]">
                {rules.map((r) => (
                  <div key={r.id} className="flex items-center gap-2">
                    {r.met ? (
                      <Check className="w-3.5 h-3.5 shrink-0 text-[#1e8e3e]" />
                    ) : (
                      <X className="w-3.5 h-3.5 shrink-0 text-[#9aa0a6]" />
                    )}
                    <span className={`text-xs ${r.met ? 'text-[#202124] dark:text-[#e8eaed]' : 'text-[#9aa0a6]'}`}>{r.label}</span>
                  </div>
                ))}
                {form.confirmPassword && !passwordsMatch && (
                  <div className="flex items-center gap-2">
                    <X className="w-3.5 h-3.5 shrink-0 text-[#d93025]" />
                    <span className="text-xs text-[#9aa0a6]">Passwords do not match</span>
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={!isValid || loading}
              className="w-full py-2.5 rounded-full text-sm font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white shadow-xs transition-all cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-60"
            >
              {loading ? 'Creating account...' : 'Create Account'}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        </div>

        <p className="text-center text-[11px] text-[#9aa0a6] mt-5">
          Already have an account?{' '}
          <Link to="/login" className="font-semibold text-[#1a73e8] dark:text-[#8ab4f8]">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
